import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Shield, ShieldOff, Key, Link2, Unlink,
  ChevronDown, ChevronUp, ToggleLeft, ToggleRight,
  UserCheck, User, Settings2, RotateCcw,
  CheckSquare, Square, AlertCircle, Search,
  Trash2, Mail,
} from 'lucide-react';
import { SortableHeader } from '../../components/ui/SortableHeader';
import { apiGet, apiPost, apiPatch, apiDelete, apiPut } from '../../api/client';
import { Avatar } from '../../components/ui/Avatar';
import { useGoogleWhitelist } from '../../api/useGoogleWhitelist';
import { Button } from '../../components/ui/Button';
import { PageHeader, type ActionDef } from '../../components/ui/PageHeader';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/Modal';
import { toast as sonnerToast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

type UserRole = 'ADMIN' | 'EDITOR' | 'VIEWER' | 'COLLABORATOR';

interface ManagedUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  authProvider: string;
  avatarUrl?: string | null;
  bio?: string | null;
  disabledAt?: string | null;
  linkedCollaboratorId?: string | null;
  linkedExternalPhotographerId?: string | null;
  linkedCollaborator?: { id: string; name: string; email: string; role: string; avatarUrl?: string | null } | null;
  linkedExternalPhotographer?: { id: string; firstName: string; lastName: string; email: string; avatarUrl?: string | null } | null;
  createdAt: string;
}

interface Collaborator {
  id: string;
  name: string;
  email: string;
  role: string;
  avatarUrl?: string | null;
  linkedUserId?: string | null;
}

interface ExternalPhotographer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  studioName?: string | null;
  avatarUrl?: string | null;
  linkedUserId?: string | null;
}

interface PermissionData {
  permissionKeys: string[];
  permissions: Record<string, Record<string, boolean>>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Amministratore',
  EDITOR: 'Editor',
  VIEWER: 'Visualizzatore',
  COLLABORATOR: 'Collaboratore',
};

const ROLE_COLORS: Record<UserRole, string> = {
  ADMIN: '#ef4444',
  EDITOR: '#3b82f6',
  VIEWER: '#8b5cf6',
  COLLABORATOR: '#22c55e',
};

const PERMISSION_LABELS: Record<string, string> = {
  // Dashboard
  'dashboard.view': 'Accesso Dashboard',
  'dashboard.kpi': 'KPI Finanziarie',
  'dashboard.charts': 'Grafici (Entrate + Stati)',
  'dashboard.feed': 'Feed Da fare + Prossimi eventi',
  'dashboard.activity': 'Attività recente + Post-prod',
  'dashboard.report': 'Report Mensile PDF',
  // Calendar
  'calendar.view': 'Vedi Calendario',
  'calendar.edit': 'Modifica Calendario',
  // Clients
  'clients.view': 'Vedi Clienti',
  'clients.create': 'Crea Clienti',
  'clients.edit': 'Modifica Clienti',
  'clients.delete': 'Elimina Clienti',
  // Events — page
  'events.view': 'Vedi Lista Eventi',
  'events.create': 'Crea Eventi',
  'events.edit': 'Modifica Eventi',
  'events.delete': 'Elimina Eventi',
  'events.financial': 'Tab Economica',
  // Events — tabs
  'events.overview.view': 'Tab Panoramica',
  'events.services.view': 'Tab Servizi (vedi)',
  'events.services.edit': 'Tab Servizi (modifica)',
  'events.scaletta.view': 'Tab Scaletta (vedi)',
  'events.scaletta.edit': 'Tab Scaletta (modifica)',
  'events.collaborators.view': 'Tab Collaboratori (vedi)',
  'events.collaborators.edit': 'Tab Collaboratori (modifica)',
  'events.post-production.view': 'Tab Post-Prod (vedi)',
  'events.post-production.edit': 'Tab Post-Prod (modifica)',
  'events.files.view': 'Tab File & Note (vedi)',
  'events.files.edit': 'Tab File & Note (modifica)',
  'events.contract.view': 'Tab Contratto (vedi)',
  'events.contract.edit': 'Tab Contratto (modifica)',
  'events.timeline.view': 'Tab Timeline',
  // Collaborators
  'collaborators.view': 'Vedi Collaboratori',
  'collaborators.manage': 'Gestisci Collaboratori',
  // Projects
  'projects.view': 'Vedi Progetti',
  'projects.manage': 'Gestisci Progetti',
  'projects.financial': 'Tab Economia Progetto',
  // External Photographers
  'external-photographers.view': 'Vedi Fotografi Esterni',
  'external-photographers.manage': 'Gestisci Fotografi Esterni',
  // Performances
  'performances.view': 'Vedi Prestazioni',
  'performances.manage': 'Gestisci Prestazioni',
  'performances.catalog.view': 'Tab Listino (vedi)',
  'performances.catalog.edit': 'Tab Listino (modifica)',
  // Catalog
  'catalog.view': 'Vedi Catalogo',
  'catalog.manage': 'Gestisci Catalogo',
  'catalog.packages.view': 'Tab Pacchetti (vedi)',
  'catalog.packages.edit': 'Tab Pacchetti (modifica)',
  'catalog.checklist.view': 'Tab Checklist (vedi)',
  'catalog.checklist.edit': 'Tab Checklist (modifica)',
  'catalog.contracts.view': 'Tab Contratti (vedi)',
  'catalog.contracts.edit': 'Tab Contratti (modifica)',
  'catalog.collaborator-templates.view': 'Tab Template Collab (vedi)',
  'catalog.collaborator-templates.edit': 'Tab Template Collab (modifica)',
  // Documents
  'documents.view': 'Vedi Documenti',
  'documents.edit': 'Modifica Documenti',
  // Reports + Finance
  'reports.view': 'Vedi Report',
  'reports.export': 'Esporta Report',
  'finance.view': 'Vedi Finanza',
  'finance.edit': 'Modifica Finanza',
  // Notifications
  'notifications.view': 'Vedi Notifiche',
  // Settings — API
  'settings.edit': 'Modifica Impostazioni (API)',
  // Settings — tabs
  'settings.studio.view': 'Tab Profilo Studio (vedi)',
  'settings.studio.edit': 'Tab Profilo Studio (modifica)',
  'settings.notifications.view': 'Tab Notifiche Impostaz. (vedi)',
  'settings.notifications.edit': 'Tab Notifiche Impostaz. (modifica)',
  'settings.calendar.view': 'Tab Calendario (vedi)',
  'settings.calendar.edit': 'Tab Calendario (modifica)',
  'settings.documents.view': 'Tab Documenti Legali (vedi)',
  'settings.documents.edit': 'Tab Documenti Legali (modifica)',
  'settings.system.view': 'Tab Sistema (vedi)',
  'settings.system.edit': 'Tab Sistema (modifica)',
  // Users
  'users.manage': 'Gestione Utenti',
};

const PERMISSION_GROUPS = [
  {
    label: 'Dashboard',
    keys: ['dashboard.view', 'dashboard.kpi', 'dashboard.charts', 'dashboard.feed', 'dashboard.activity', 'dashboard.report'],
  },
  {
    label: 'Calendario',
    keys: ['calendar.view', 'calendar.edit'],
  },
  {
    label: 'Clienti',
    keys: ['clients.view', 'clients.create', 'clients.edit', 'clients.delete'],
  },
  {
    label: 'Collaboratori',
    keys: ['collaborators.view', 'collaborators.manage'],
  },
  {
    label: 'Fotografi Esterni',
    keys: ['external-photographers.view', 'external-photographers.manage'],
  },
  {
    label: 'Progetti',
    keys: ['projects.view', 'projects.manage', 'projects.financial'],
  },
  {
    label: 'Prestazioni',
    keys: ['performances.view', 'performances.manage', 'performances.catalog.view', 'performances.catalog.edit'],
  },
  {
    label: 'Catalogo — Pagina',
    keys: ['catalog.view', 'catalog.manage'],
  },
  {
    label: 'Catalogo — Tab',
    keys: [
      'catalog.packages.view', 'catalog.packages.edit',
      'catalog.checklist.view', 'catalog.checklist.edit',
      'catalog.contracts.view', 'catalog.contracts.edit',
      'catalog.collaborator-templates.view', 'catalog.collaborator-templates.edit',
    ],
  },
  {
    label: 'Eventi — Pagina',
    keys: ['events.view', 'events.create', 'events.edit', 'events.delete', 'events.financial'],
  },
  {
    label: 'Eventi — Tab',
    keys: [
      'events.overview.view',
      'events.services.view', 'events.services.edit',
      'events.scaletta.view', 'events.scaletta.edit',
      'events.collaborators.view', 'events.collaborators.edit',
      'events.post-production.view', 'events.post-production.edit',
      'events.files.view', 'events.files.edit',
      'events.contract.view', 'events.contract.edit',
      'events.timeline.view',
    ],
  },
  {
    label: 'Documenti',
    keys: ['documents.view', 'documents.edit'],
  },
  {
    label: 'Report e Finanza',
    keys: ['reports.view', 'reports.export', 'finance.view', 'finance.edit'],
  },
  {
    label: 'Notifiche',
    keys: ['notifications.view'],
  },
  {
    label: 'Impostazioni — Tab',
    keys: [
      'settings.edit',
      'settings.studio.view', 'settings.studio.edit',
      'settings.notifications.view', 'settings.notifications.edit',
      'settings.calendar.view', 'settings.calendar.edit',
      'settings.documents.view', 'settings.documents.edit',
      'settings.system.view', 'settings.system.edit',
    ],
  },
  {
    label: 'Utenti',
    keys: ['users.manage'],
  },
];

const cardStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  padding: '0.875rem 1.25rem',
  background: 'var(--glass-bg-card)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem 0.75rem',
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', outline: 'none',
};

// ─── User Row ─────────────────────────────────────────────────────────────────

function UserRow({
  user, onRoleChange, onToggleDisable, onResetPassword, onLinkCollaborator, onLinkExtPhotographer, onDelete,
}: {
  user: ManagedUser;
  onRoleChange: (id: string, role: UserRole) => void;
  onToggleDisable: (id: string) => void;
  onResetPassword: (id: string) => void;
  onLinkCollaborator: (user: ManagedUser) => void;
  onLinkExtPhotographer: (user: ManagedUser) => void;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const disabled = !!user.disabledAt;

  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      overflow: 'hidden',
      opacity: disabled ? 0.65 : 1,
    }}>
      {/* Main row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.875rem',
        padding: '0.75rem 1rem', background: 'var(--glass-bg-card)',
      }}>
        <Avatar src={user.avatarUrl} name={user.name} size={38} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}>{user.name}</span>
            <span style={{
              padding: '0.15rem 0.6rem', borderRadius: 'var(--radius-interactive)',
              fontSize: 'var(--text-xs)', fontWeight: 700,
              background: ROLE_COLORS[user.role], color: '#FFFFFF',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {ROLE_LABELS[user.role]}
            </span>
            {disabled && (
              <span style={{
                fontSize: 'var(--text-xs)', padding: '0.1rem 0.5rem',
                background: '#EF4444', color: '#FFFFFF', borderRadius: 'var(--radius-interactive)', fontWeight: 600,
                whiteSpace: 'nowrap',
              }}>
                Disabilitato
              </span>
            )}
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email} · {user.authProvider === 'GOOGLE' ? 'Google' : 'Password'}
          </p>
          {(user.linkedCollaborator || user.linkedExternalPhotographer) && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.2rem' }}>
              {user.linkedCollaborator && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Link2 size={11} /> {user.linkedCollaborator.name}
                </span>
              )}
              {user.linkedExternalPhotographer && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <UserCheck size={11} /> {user.linkedExternalPhotographer.firstName} {user.linkedExternalPhotographer.lastName}
                </span>
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-muted)', padding: '0.25rem', flexShrink: 0,
          }}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Expanded actions */}
      {expanded && (
        <div style={{
          padding: '0.875rem 1rem', background: 'var(--glass-bg-card)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', flexDirection: 'column', gap: '0.875rem',
        }}>
          {/* Role selector */}
          <div>
            <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.375rem' }}>
              Ruolo
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {(Object.keys(ROLE_LABELS) as UserRole[]).map(r => (
                <button
                  key={r}
                  onClick={() => onRoleChange(user.id, r)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: 'var(--radius-interactive)',
                    border: `1px solid ${r === user.role ? ROLE_COLORS[r] : 'rgba(255,255,255,0.10)'}`,
                    cursor: 'pointer',
                    fontSize: 'var(--text-xs)', fontWeight: r === user.role ? 700 : 400,
                    background: r === user.role ? `${ROLE_COLORS[r]}15` : 'var(--glass-bg-card)',
                    color: r === user.role ? ROLE_COLORS[r] : 'var(--color-text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          {/* Actions row */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button size="sm" variant="secondary"
              icon={disabled ? <ToggleRight size={13} style={{ color: '#22c55e' }} /> : <ToggleLeft size={13} />}
              onClick={() => onToggleDisable(user.id)}>
              {disabled ? 'Riabilita' : 'Disabilita'}
            </Button>

            {user.authProvider === 'LOCAL' && (
              <Button size="sm" variant="secondary" icon={<Key size={13} />}
                onClick={() => onResetPassword(user.id)}>
                Reset password
              </Button>
            )}

            <Button size="sm" variant="secondary" icon={<Link2 size={13} />}
              onClick={() => onLinkCollaborator(user)}>
              {user.linkedCollaboratorId ? 'Cambia collaboratore' : 'Collega collaboratore'}
            </Button>

            <Button size="sm" variant="secondary" icon={<UserCheck size={13} />}
              onClick={() => onLinkExtPhotographer(user)}>
              {user.linkedExternalPhotographerId ? 'Cambia fotografo' : 'Collega fotografo'}
            </Button>

            {(user.linkedCollaboratorId || user.linkedExternalPhotographerId) && (
              <Button size="sm" variant="ghost" icon={<Unlink size={13} />}
                onClick={async () => {
                  if (user.linkedCollaboratorId)
                    await apiPost(`/users/${user.id}/link-collaborator`, { collaboratorId: null });
                  if (user.linkedExternalPhotographerId)
                    await apiPost(`/users/${user.id}/link-external-photographer`, { externalPhotographerId: null });
                  toast.success('Collegamento rimosso');
                }}>
                Scollega
              </Button>
            )}

            <Button size="sm" variant="ghost" icon={<Trash2 size={13} />}
              onClick={() => onDelete(user.id)}
              style={{ color: 'var(--color-error)' }}>
              Elimina
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Permissions Panel ────────────────────────────────────────────────────────

function PermissionsPanel() {
  const qc = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<UserRole>('EDITOR');
  const [localPerms, setLocalPerms] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);

  const { data: permData, isLoading } = useQuery<PermissionData>({
    queryKey: ['role-permissions'],
    queryFn: () => apiGet<PermissionData>('/role-permissions'),
  });

  React.useEffect(() => {
    if (permData?.permissions[selectedRole]) {
      setLocalPerms(
        Object.fromEntries(
          Object.entries(permData.permissions[selectedRole] ?? {})
            .filter(([, v]) => typeof v === 'boolean')
        ) as Record<string, boolean>
      );
      setDirty(false);
    }
  }, [permData, selectedRole]);

  const saveMutation = useMutation({
    mutationFn: (perms: Record<string, boolean>) => {
      const cleanPerms = Object.fromEntries(
        Object.entries(perms).filter(([, v]) => typeof v === 'boolean')
      );
      return apiPut(`/role-permissions/${selectedRole}`, cleanPerms);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['role-permissions'] });
      setDirty(false);
      toast.success('Permessi salvati');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetMutation = useMutation({
    mutationFn: () => apiPost(`/role-permissions/${selectedRole}/reset`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['role-permissions'] });
      setDirty(false);
      toast.success('Permessi ripristinati ai valori predefiniti');
    },
  });

  const toggle = (key: string) => {
    if (selectedRole === 'ADMIN') return;
    setLocalPerms(p => ({ ...p, [key]: !p[key] }));
    setDirty(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.25rem' }}>
      {/* Role tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {(Object.keys(ROLE_LABELS) as UserRole[]).map(r => (
          <button
            key={r}
            onClick={() => setSelectedRole(r)}
            style={{
              padding: '0.375rem 0.875rem',
              borderRadius: 'var(--radius-card)',
              border: `1px solid ${r === selectedRole ? ROLE_COLORS[r] : 'rgba(255,255,255,0.10)'}`,
              cursor: 'pointer',
              fontSize: 'var(--text-sm)', fontWeight: r === selectedRole ? 700 : 400,
              background: r === selectedRole ? `${ROLE_COLORS[r]}15` : 'var(--glass-bg-card)',
              color: r === selectedRole ? ROLE_COLORS[r] : 'var(--color-text-muted)',
              transition: 'all 0.15s',
            }}
          >
            {ROLE_LABELS[r]}
          </button>
        ))}
      </div>

      {selectedRole === 'ADMIN' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.625rem 0.875rem', background: '#ef444415',
          borderRadius: 'var(--radius-card)', border: '1px solid #ef444430',
        }}>
          <AlertCircle size={14} style={{ color: 'var(--color-error)', flexShrink: 0 }} />
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)' }}>
            L'amministratore ha sempre tutti i permessi attivi e non sono modificabili.
          </p>
        </div>
      )}

      {isLoading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento...</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {PERMISSION_GROUPS.map(group => (
            <div key={group.label}>
              <p style={{
                fontSize: 'var(--text-xs)', fontWeight: 700,
                color: 'var(--color-text-muted)', textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: '0.5rem',
              }}>
                {group.label}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.375rem' }}>
                {group.keys.map(key => {
                  const locked = selectedRole === 'ADMIN';
                  const enabled = localPerms[key] ?? false;
                  return (
                    <button
                      key={key}
                      onClick={() => toggle(key)}
                      disabled={locked}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: enabled ? 'oklch(from var(--color-primary) l c h / 0.08)' : 'var(--glass-bg-card)',
                        border: `1px solid ${enabled ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                        borderRadius: 'var(--radius-interactive)',
                        cursor: locked ? 'not-allowed' : 'pointer',
                        opacity: locked ? 0.5 : 1,
                        transition: 'all 0.15s',
                        textAlign: 'left',
                      }}
                    >
                      {enabled
                        ? <CheckSquare size={14} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                        : <Square size={14} style={{ color: 'var(--color-text-faint)', flexShrink: 0 }} />
                      }
                      <span style={{
                        fontSize: 'var(--text-xs)',
                        color: enabled ? 'var(--color-text)' : 'var(--color-text-muted)',
                        fontWeight: enabled ? 600 : 400,
                      }}>
                        {PERMISSION_LABELS[key] ?? key}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', paddingTop: '0.5rem' }}>
            <Button size="sm" variant="ghost" icon={<RotateCcw size={13} />}
              loading={resetMutation.isPending}
              onClick={() => {
                if (confirm('Ripristinare i permessi predefiniti per questo ruolo?'))
                  resetMutation.mutate();
              }}>
              Ripristina default
            </Button>
            <Button size="sm" disabled={!dirty} loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate(localPerms)}>
              Salva permessi
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Google OAuth Whitelist Panel ─────────────────────────────────────────────

function GoogleWhitelistPanel() {
  const { list, add, remove } = useGoogleWhitelist();
  const [newEmail, setNewEmail] = useState('');

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }
    add.mutate(email, {
      onSuccess: () => setNewEmail(''),
      onError: (err: Error) => toast.error(err.message),
    });
  };

  const handleRemove = (email: string) => {
    if (!confirm(`Rimuovere ${email} dalla whitelist?`)) return;
    remove.mutate(email, {
      onError: (err: Error) => toast.error(err.message),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.25rem' }}>
      {/* Info banner */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
        padding: '0.75rem 1rem',
        background: 'oklch(from var(--color-primary) l c h / 0.06)',
        borderRadius: 'var(--radius-card)',
        border: '1px solid oklch(from var(--color-primary) l c h / 0.15)',
      }}>
        <AlertCircle size={15} style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 1.5 }}>
          <strong>Whitelist Google OAuth.</strong> Solo le email in questa lista possono accedere con "Accedi con Google".
          Se la lista è vuota, l'accesso Google è disabilitato per tutti.
          Gli admin possono sempre accedere con email e password.
        </div>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="email"
          placeholder="es. mario.rossi@gmail.com"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          style={{
            flex: 1, padding: '0.5rem 0.75rem',
            fontSize: 'var(--text-sm)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--glass-bg-card)',
            color: 'var(--color-text)',
            outline: 'none',
          }}
        />
        <Button type="submit" size="sm" icon={<Plus size={14} />} loading={add.isPending}>
          Aggiungi
        </Button>
      </form>

      {/* Whitelist entries */}
      {list.isLoading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento...</p>
      ) : list.data && list.data.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {list.data.map(entry => (
            <div
              key={entry.id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.625rem 0.875rem',
                background: 'var(--glass-bg-card)',
                borderRadius: 'var(--radius-card)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Mail size={14} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', fontWeight: 500 }}>
                  {entry.email}
                </span>
              </div>
              <button
                onClick={() => handleRemove(entry.email)}
                disabled={remove.isPending}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-text-muted)', padding: '0.25rem',
                  borderRadius: 'var(--radius-interactive)',
                  display: 'flex', alignItems: 'center',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ef4444'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--color-text-muted)'; }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Nessuna email in whitelist. L'accesso Google è disabilitato per tutti.
        </p>
      )}
    </div>
  );
}

// ─── Sort & Filter Helpers ────────────────────────────────────────────────────

type UserSortField = 'name' | 'email' | 'role';
type SortOrder = 'asc' | 'desc';

function sortUsers(list: ManagedUser[], field: UserSortField, order: SortOrder) {
  return [...list].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'name':  cmp = a.name.localeCompare(b.name); break;
      case 'email': cmp = a.email.localeCompare(b.email); break;
      case 'role':  cmp = a.role.localeCompare(b.role); break;
    }
    return order === 'asc' ? cmp : -cmp;
  });
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function UserManagementPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'users' | 'permissions' | 'google-oauth'>('users');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<UserSortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const handleSort = (field: UserSortField) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [linkCollaboratorFor, setLinkCollaboratorFor] = useState<ManagedUser | null>(null);
  const [linkExtPhotographerFor, setLinkExtPhotographerFor] = useState<ManagedUser | null>(null);
  const [resetPwFor, setResetPwFor] = useState<string | null>(null);
  const [newPw, setNewPw] = useState('');
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  // Match-check flow
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [matchData, setMatchData] = useState<{
    collaborators: Array<{ id: string; name: string; email: string; role: string }>;
    externalPhotographers: Array<{ id: string; firstName: string; lastName: string; email: string }>;
  }>({ collaborators: [], externalPhotographers: [] });
  const [selectedCollabId, setSelectedCollabId] = useState<string | null>(null);
  const [selectedExtId, setSelectedExtId] = useState<string | null>(null);
  const [checkingMatches, setCheckingMatches] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    name: '', email: '', password: '', role: 'EDITOR' as UserRole, authProvider: 'LOCAL' as string,
  });

  const { data: rawUsers = [], isLoading } = useQuery<ManagedUser[]>({
    queryKey: ['users'],
    queryFn: () => apiGet<ManagedUser[]>('/users'),
  });

  const users = useMemo(() => {
    const filtered = search
      ? rawUsers.filter(u =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()))
      : rawUsers;
    return sortUsers(filtered, sortBy, sortOrder);
  }, [rawUsers, search, sortBy, sortOrder]);

  const { data: collaborators = [] } = useQuery<Collaborator[]>({
    queryKey: ['collaborators-link'],
    queryFn: async () => {
      const res = await apiGet<Collaborator[]>('/collaborators?limit=200&active=true');
      return res ?? [];
    },
    enabled: !!linkCollaboratorFor,
  });

  const { data: extPhotographers = [] } = useQuery<ExternalPhotographer[]>({
    queryKey: ['ext-photographers-link'],
    queryFn: async () => {
      const res = await apiGet<ExternalPhotographer[]>('/external-photographers?limit=200&active=true');
      return res ?? [];
    },
    enabled: !!linkExtPhotographerFor,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof createForm & { linkCollaboratorId?: string | null; linkExternalPhotographerId?: string | null; skipAutoLink?: boolean }) => apiPost('/users', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
      setCreateForm({ name: '', email: '', password: '', role: 'EDITOR', authProvider: 'LOCAL' });
      toast.success('Utente creato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) =>
      apiPut(`/users/${id}/role`, { role }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); toast.success('Ruolo aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const disableMutation = useMutation({
    mutationFn: (id: string) => apiPut(`/users/${id}/disable`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); toast.success('Stato account aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetPwMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      apiPatch(`/users/${id}/password`, { password }),
    onSuccess: () => {
      setResetPwFor(null);
      setNewPw('');
      toast.success('Password aggiornata');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const linkCollaboratorMutation = useMutation({
    mutationFn: ({ userId, collaboratorId }: { userId: string; collaboratorId: string | null }) =>
      apiPost(`/users/${userId}/link-collaborator`, { collaboratorId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setLinkCollaboratorFor(null);
      toast.success('Collaboratore collegato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const linkExtPhotographerMutation = useMutation({
    mutationFn: ({ userId, externalPhotographerId }: { userId: string; externalPhotographerId: string | null }) =>
      apiPost(`/users/${userId}/link-external-photographer`, { externalPhotographerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setLinkExtPhotographerFor(null);
      toast.success('Fotografo collegato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); sonnerToast.success('Utente eliminato'); },
    onError: (e: Error) => sonnerToast.error(e.message),
  });

  const doCreate = (linkCollaboratorId?: string | null, linkExternalPhotographerId?: string | null, skipAutoLink?: boolean) => {
    createMutation.mutate({
      ...createForm,
      linkCollaboratorId: linkCollaboratorId ?? null,
      linkExternalPhotographerId: linkExternalPhotographerId ?? null,
      skipAutoLink,
    });
    setMatchDialogOpen(false);
  };

  const handleCreateClick = async () => {
    if (!createForm.name.trim() || !createForm.email.trim()) {
      toast.error('Nome e email sono obbligatori');
      return;
    }
    if (createForm.authProvider as string !== 'GOOGLE' && !createForm.password) {
      toast.error('Password obbligatoria');
      return;
    }

    setCheckingMatches(true);
    try {
      const params = new URLSearchParams();
      if (createForm.email) params.set('email', createForm.email);
      if (createForm.name) params.set('name', createForm.name);
      const res = await apiGet<{ collaborators: any[]; externalPhotographers: any[] }>(`/users/check-matches?${params.toString()}`);
      const data = res && typeof res === 'object' && 'collaborators' in (res as any) ? res : { collaborators: [], externalPhotographers: [] };
      const collabs = data.collaborators ?? [];
      const exts = data.externalPhotographers ?? [];

      if (collabs.length > 0 || exts.length > 0) {
        setMatchData({ collaborators: collabs, externalPhotographers: exts });
        setSelectedCollabId(collabs.length === 1 ? collabs[0].id : null);
        setSelectedExtId(exts.length === 1 ? exts[0].id : null);
        setMatchDialogOpen(true);
      } else {
        doCreate(null, null, false);
      }
    } catch {
      doCreate(null, null, false);
    } finally {
      setCheckingMatches(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.25rem', overflow: 'hidden' }}>

      <PageHeader
        title="Gestione Utenti"
        subtitle={`${rawUsers.length} utent${rawUsers.length !== 1 ? 'i' : 'e'} registrat${rawUsers.length !== 1 ? 'i' : 'o'}`}
        actions={[{ label: 'Nuovo utente', icon: <Plus size={15} />, onClick: () => setShowCreateModal(true), variant: 'primary' }]}
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.375rem', paddingBottom: '0.5rem' }}>
        {([
          { key: 'users', label: 'Utenti', icon: <User size={15} /> },
          { key: 'permissions', label: 'Permessi ruoli', icon: <Settings2 size={15} /> },
          { key: 'google-oauth' as const, label: 'Google OAuth', icon: <Shield size={15} /> },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 1rem',
              background: activeTab === tab.key ? 'var(--color-primary-highlight)' : 'transparent',
              border: activeTab === tab.key ? '1px solid rgba(79,152,163,0.3)' : '1px solid transparent',
              borderRadius: 'var(--radius-interactive)',
              cursor: 'pointer',
              color: activeTab === tab.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontSize: 'var(--text-sm)', fontWeight: activeTab === tab.key ? 600 : 400,
              transition: 'all var(--spring-duration) var(--spring-easing)',
            }}
          >
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Utenti */}
      {activeTab === 'users' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>

          {/* Search + Sort bar */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 380 }}>
              <Search size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
              <input
                type="search"
                placeholder="Cerca per nome o email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  background: 'var(--glass-bg-card)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 'var(--radius-card)',
                  color: 'var(--color-text)',
                  fontSize: 'var(--text-sm)',
                  padding: '0.5rem 0.75rem 0.5rem 2.25rem',
                  width: '100%', outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              Ordina:
              <SortableHeader label="Nome" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <SortableHeader label="Email" field="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <SortableHeader label="Ruolo" field="role" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
            </div>
          </div>
          {isLoading ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '1rem' }}>Caricamento...</p>
          ) : users.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '1rem' }}>Nessun utente.</p>
          ) : (
            users.map(u => (
              <UserRow
                key={u.id}
                user={u}
                onRoleChange={(id, role) => roleMutation.mutate({ id, role })}
                onToggleDisable={id => disableMutation.mutate(id)}
                onResetPassword={id => setResetPwFor(id)}
                onLinkCollaborator={u => setLinkCollaboratorFor(u)}
                onLinkExtPhotographer={u => setLinkExtPhotographerFor(u)}
                onDelete={id => setDeleteUserId(id)}
              />
            ))
          )}
        </div>
      )}

      {/* Tab: Permessi */}
      {activeTab === 'permissions' && (
        <div style={cardStyle}>
          <div style={headerStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <Shield size={16} style={{ color: 'var(--color-primary)' }} />
              <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>Permessi per ruolo</span>
            </div>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
              Configura le azioni consentite per ciascun ruolo. Le modifiche si applicano a tutti gli utenti con quel ruolo.
            </p>
          </div>
          <PermissionsPanel />
        </div>
      )}

      {/* Tab: Google OAuth */}
      {activeTab === 'google-oauth' && <GoogleWhitelistPanel />}

      {/* ── Modal: Crea utente ─────────────────────────────────────────────── */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} title="Nuovo utente">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
              Nome
            </label>
            <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
              Email
            </label>
            <input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
              Password (min. 6 caratteri)
            </label>
            <input type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
              Ruolo
            </label>
            <select
              value={createForm.role}
              onChange={e => setCreateForm(f => ({ ...f, role: e.target.value as UserRole }))}
              style={inputStyle}
            >
              {(Object.keys(ROLE_LABELS) as UserRole[]).map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Annulla</Button>
            <Button loading={createMutation.isPending || checkingMatches} onClick={handleCreateClick}>
              Crea utente
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Dialog: Match trovati ─────────────────────────────────────────── */}
      <Modal open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} title="Collegamento trovato" width={480}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setMatchDialogOpen(false)}>Annulla</Button>
            <Button variant="ghost" size="sm" onClick={() => doCreate(null, null, true)}>
              Salta collegamenti
            </Button>
            <Button size="sm" loading={createMutation.isPending} onClick={() => doCreate(selectedCollabId, selectedExtId, false)}>
              Crea e collega
            </Button>
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
          <p>
            L'email o il nome <strong>{createForm.email || createForm.name}</strong> corrisponde a:
          </p>
          {matchData.collaborators.map(c => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', border: selectedCollabId === c.id ? '1px solid var(--color-primary)' : '1px solid rgba(255,255,255,0.10)' }}>
              <input type="radio" name="linkCollab" checked={selectedCollabId === c.id} onChange={() => setSelectedCollabId(c.id)} />
              <div>
                <strong>{c.name}</strong>
                <span style={{ color: 'var(--color-text-muted)', marginLeft: '0.5rem' }}>(Collaboratore · {c.role})</span>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{c.email}</div>
              </div>
            </label>
          ))}
          {matchData.externalPhotographers.map(ep => (
            <label key={ep.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', border: selectedExtId === ep.id ? '1px solid var(--color-primary)' : '1px solid rgba(255,255,255,0.10)' }}>
              <input type="radio" name="linkExt" checked={selectedExtId === ep.id} onChange={() => setSelectedExtId(ep.id)} />
              <div>
                <strong>{ep.firstName} {ep.lastName}</strong>
                <span style={{ color: 'var(--color-text-muted)', marginLeft: '0.5rem' }}>(Fotografo esterno)</span>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{ep.email}</div>
              </div>
            </label>
          ))}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Seleziona chi collegare, poi clicca <strong>Crea e collega</strong>. Oppure <strong>Salta collegamenti</strong> per creare l'utente senza collegamenti.
          </p>
        </div>
      </Modal>

      {/* ── Modal: Reset password ──────────────────────────────────────────── */}
      <Modal open={!!resetPwFor} onClose={() => { setResetPwFor(null); setNewPw(''); }} title="Reset password">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Imposta una nuova password per questo utente. Le sessioni attive verranno terminate.
          </p>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
              Nuova password
            </label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} style={inputStyle} placeholder="min. 6 caratteri" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => { setResetPwFor(null); setNewPw(''); }}>Annulla</Button>
            <Button loading={resetPwMutation.isPending}
              onClick={() => resetPwFor && resetPwMutation.mutate({ id: resetPwFor, password: newPw })}>
              Imposta password
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Modal: Collega collaboratore ───────────────────────────────────── */}
      {linkCollaboratorFor && (
        <Modal open onClose={() => setLinkCollaboratorFor(null)} title={`Collega collaboratore — ${linkCollaboratorFor.name}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: 400, overflowY: 'auto' }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
              Seleziona il profilo collaboratore da collegare a questo account utente.
            </p>
            {(collaborators as Collaborator[]).map((c: Collaborator) => (
              <button
                key={c.id}
                onClick={() => linkCollaboratorMutation.mutate({ userId: linkCollaboratorFor.id, collaboratorId: c.id })}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.625rem 0.875rem',
                  background: c.id === linkCollaboratorFor.linkedCollaboratorId ? 'oklch(from var(--color-primary) l c h / 0.08)' : 'var(--glass-bg-card)',
                  border: `1px solid ${c.id === linkCollaboratorFor.linkedCollaboratorId ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                  borderRadius: 'var(--radius-card)', cursor: 'pointer', textAlign: 'left',
                  opacity: c.linkedUserId && c.linkedUserId !== linkCollaboratorFor.id ? 0.5 : 1,
                  color: 'var(--color-text)',
                }}
                disabled={!!c.linkedUserId && c.linkedUserId !== linkCollaboratorFor.id}
              >
                <Avatar src={c.avatarUrl} name={c.name} size={32} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{c.name}</p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{c.email} · {c.role}</p>
                  {c.linkedUserId && c.linkedUserId !== linkCollaboratorFor.id && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>Già collegato ad altro utente</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* ── Modal: Collega fotografo esterno ──────────────────────────────── */}
      {linkExtPhotographerFor && (
        <Modal open onClose={() => setLinkExtPhotographerFor(null)} title={`Collega fotografo — ${linkExtPhotographerFor.name}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: 400, overflowY: 'auto' }}>
            {(extPhotographers as ExternalPhotographer[]).map((ep: ExternalPhotographer) => (
              <button
                key={ep.id}
                onClick={() => linkExtPhotographerMutation.mutate({ userId: linkExtPhotographerFor.id, externalPhotographerId: ep.id })}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.625rem 0.875rem',
                  background: ep.id === linkExtPhotographerFor.linkedExternalPhotographerId ? 'oklch(from var(--color-primary) l c h / 0.08)' : 'var(--glass-bg-card)',
                  border: `1px solid ${ep.id === linkExtPhotographerFor.linkedExternalPhotographerId ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                  borderRadius: 'var(--radius-card)', cursor: 'pointer', textAlign: 'left',
                  opacity: ep.linkedUserId && ep.linkedUserId !== linkExtPhotographerFor.id ? 0.5 : 1,
                  color: 'var(--color-text)',
                }}
                disabled={!!ep.linkedUserId && ep.linkedUserId !== linkExtPhotographerFor.id}
              >
                <Avatar src={ep.avatarUrl} name={`${ep.firstName} ${ep.lastName}`} size={32} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{ep.firstName} {ep.lastName}</p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {ep.email}{ep.studioName && ` · ${ep.studioName}`}
                  </p>
                  {ep.linkedUserId && ep.linkedUserId !== linkExtPhotographerFor.id && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>Già collegato ad altro utente</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!deleteUserId}
        onClose={() => setDeleteUserId(null)}
        onConfirm={() => { if (deleteUserId) deleteMutation.mutate(deleteUserId); setDeleteUserId(null); }}
        title="Elimina utente"
        description="L'utente verrà eliminato definitivamente. Questa azione non può essere annullata."
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
